import AdminAiFeaturesModuleCard from "../components/ai-scans/AdminAiFeaturesModuleCard";
import AdminAiScanPageStatus from "../components/ai-scans/AdminAiScanPageStatus";
import { useAdminAiScanReports } from "../hooks/useAdminAiScanReports";
import type { AdminAiFeaturesExpansion } from "../models/admin-ai-features.models";
import { adminAiScansStyles } from "../styles/admin-ai-scans.styles";
import {
  filterAiFeaturesModules,
  prepareAiFeaturesModules,
  totalAiFeaturesCounts,
} from "../utils/admin-ai-features.utils";
import { useMemo, useState } from "react";

// Own the feature inventory's filters and expansion commands without placing report state in the tab outlet.
// Counts follow the visible hierarchy; complete source JSON remains available inside each module.
const AdminAiFeaturesScanPage = () => {
  const { reports, issues, loading, error, refresh, available } = useAdminAiScanReports("features");
  const [search, setSearch] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [expansion, setExpansion] = useState<AdminAiFeaturesExpansion>("tree");
  const [expansionRevision, setExpansionRevision] = useState(0);
  const modules = useMemo(() => prepareAiFeaturesModules(reports), [reports]);
  const moduleIds = Array.from(new Set(modules.map((module) => module.moduleId)));
  const selectedModuleId = moduleIds.includes(moduleId) ? moduleId : "";
  const visibleModules = useMemo(
    () => filterAiFeaturesModules(modules, selectedModuleId, search),
    [modules, selectedModuleId, search],
  );
  const counts = totalAiFeaturesCounts(visibleModules);
  const metrics = [
    { key: "modules", label: "Modules", value: visibleModules.length },
    { key: "businessObjects", label: "Business objects", value: counts.businessObjects },
    { key: "surfaces", label: "Surfaces", value: counts.surfaces },
    { key: "featureGroups", label: "Feature groups", value: counts.featureGroups },
    { key: "behaviors", label: "Behaviors", value: counts.behaviors },
  ];

  const expandInventory = (next: AdminAiFeaturesExpansion) => {
    setExpansion(next);
    setExpansionRevision((revision) => revision + 1);
  };

  return (
    <section data-test="admin-ai-features-scan-page" className="space-y-3" aria-labelledby="admin-ai-features-title">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <h2 id="admin-ai-features-title" className="py-2 text-base font-semibold">
          Features
        </h2>
        <AdminAiScanPageStatus
          loading={loading}
          error={error}
          issues={issues}
          available={available}
          hasReports={reports.length > 0}
          onRefresh={refresh}
        />
      </header>
      {modules.length > 0 && (
        <>
          <div className="flex flex-wrap items-end gap-3" data-test="admin-ai-features-filters">
            <label className="min-w-[220px] flex-1 space-y-1 text-xs">
              <span>Search inventory</span>
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Module, business object, surface, group, behavior, or ID"
                className={`${adminAiScansStyles.control} w-full`}
                data-test="admin-ai-features-search"
              />
            </label>
            <label className="space-y-1 text-xs">
              <span>Module</span>
              <select
                value={selectedModuleId}
                onChange={(event) => setModuleId(event.target.value)}
                className={`${adminAiScansStyles.control} block min-w-[180px]`}
                data-test="admin-ai-features-module-filter"
              >
                <option value="">All modules</option>
                {moduleIds.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <dl className="grid grid-cols-2 gap-2 xl:grid-cols-5" data-test="admin-ai-features-metrics">
            {metrics.map((metric) => (
              <div
                key={metric.key}
                className="flex min-w-0 flex-wrap items-baseline justify-between gap-x-2 bg-white px-3 py-2 shadow-sm dark:bg-gray-800"
              >
                <dt className="text-xs opacity-60">{metric.label}</dt>
                <dd
                  className="text-lg font-semibold tabular-nums"
                  data-test={`admin-ai-features-count-${metric.key}`}
                >
                  {metric.value.toLocaleString()}
                </dd>
              </div>
            ))}
          </dl>
          <div className="flex flex-wrap items-center gap-2" data-test="admin-ai-features-expansion-controls">
            <button
              type="button"
              onClick={() => expandInventory("tree")}
              className={adminAiScansStyles.button}
              data-test="admin-ai-features-expand-tree"
            >
              Expand tree
            </button>
            <button
              type="button"
              onClick={() => expandInventory("behaviors")}
              className={adminAiScansStyles.button}
              data-test="admin-ai-features-expand-behaviors"
            >
              Expand all behaviors
            </button>
            <button
              type="button"
              onClick={() => expandInventory("collapsed")}
              className={adminAiScansStyles.button}
              data-test="admin-ai-features-collapse-all"
            >
              Collapse all
            </button>
          </div>
          <div key={expansionRevision} className="space-y-4" data-test="admin-ai-features-tree">
            {visibleModules.map((module) => (
              <AdminAiFeaturesModuleCard key={module.file.path} module={module} expansion={expansion} />
            ))}
            {visibleModules.length === 0 && (
              <p role="status" className={adminAiScansStyles.panel}>
                No inventory matches the current filters.
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default AdminAiFeaturesScanPage;
